import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getUser } from "@/lib/storage";
import { BrandHeader } from "@/components/BrandHeader";

const JOEY_SMILE = "https://customer-assets.emergentagent.com/job_648de01e-2b76-4a92-ab16-261bd9620d49/artifacts/tzi0vidg_3.png";
const JOEY_THINKING = "/joey-thinking.jpeg";

const STARTER_QUESTIONS = [
  "Should I trademark my course name?",
  "What's the difference between an LLC and a trademark?",
  "I think someone's copying my brand — what do I do?",
  "Is my brand name strong enough to protect?",
];

// Render markdown links [text](url) as <a>, preserve paragraphs and "→ Next step:" heading
const renderContent = (text) => {
  if (!text) return null;
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const isCTA = block.trim().startsWith("→ Next step");
    const lineParts = [];
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIdx = 0;
    let m;
    let keySeed = 0;
    while ((m = linkRegex.exec(block)) !== null) {
      if (m.index > lastIdx) lineParts.push(block.slice(lastIdx, m.index));
      const isInternal = m[2].startsWith("/");
      if (isInternal) {
        lineParts.push(
          <Link key={`l-${bi}-${keySeed++}`} to={m[2]} style={{ color: "var(--cobalt)", fontWeight: 700 }}>
            {m[1]}
          </Link>
        );
      } else {
        lineParts.push(
          <a key={`l-${bi}-${keySeed++}`} href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: "var(--cobalt)", fontWeight: 700 }}>
            {m[1]}
          </a>
        );
      }
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < block.length) lineParts.push(block.slice(lastIdx));

    // Render italic *text*
    const finalParts = lineParts.map((p, i) => {
      if (typeof p !== "string") return p;
      const segs = p.split(/(\*[^*]+\*)/g);
      return segs.map((seg, j) =>
        seg.startsWith("*") && seg.endsWith("*") && seg.length > 2
          ? <em key={`em-${bi}-${i}-${j}`}>{seg.slice(1, -1)}</em>
          : <span key={`tx-${bi}-${i}-${j}`}>{seg}</span>
      );
    });

    return (
      <p
        key={`b-${bi}`}
        style={{
          margin: bi === 0 ? "0 0 0.65rem" : "0.65rem 0",
          lineHeight: 1.55,
          background: isCTA ? "var(--warm-white-soft)" : "transparent",
          borderLeft: isCTA ? "3px solid var(--yellow)" : "none",
          padding: isCTA ? "0.6rem 0.8rem" : 0,
          borderRadius: isCTA ? 2 : 0,
          fontWeight: isCTA ? 600 : 400,
        }}
      >
        {finalParts}
      </p>
    );
  });
};

export const Ask = () => {
  const navigate = useNavigate();
  const [user, setUserState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const endRef = useRef(null);

  useEffect(() => {
    const u = getUser();
    if (!u) { navigate("/", { replace: true }); return; }
    setUserState(u);
    (async () => {
      try {
        const res = await api.get("/ask/session");
        setMessages(res.data.messages || []);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't load the conversation.");
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, sending]);

  const send = async (e, override) => {
    e?.preventDefault?.();
    const text = (override ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    // Optimistically append user message
    const nowIso = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: text, ts: nowIso }]);
    try {
      const res = await api.post("/ask/message", { message: text });
      setMessages((prev) => [...prev, res.data.message]);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't get an answer. Try again.");
      setMessages((prev) => prev.filter((_, i) => i !== prev.length - 1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const sendStarter = (q) => send(null, q);

  const clearChat = async () => {
    if (!window.confirm("Start a fresh conversation? Your current chat will be cleared.")) return;
    try {
      const res = await api.delete("/ask/session");
      setMessages(res.data.messages || []);
      toast.success("Fresh conversation started.");
    } catch (err) {
      toast.error("Couldn't reset the chat.");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(e);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="ask-page">
      <BrandHeader showSignOut />

      <section style={{ padding: "2rem 0 1.5rem" }}>
        <div className="container-narrow">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <div>
              <p className="eyebrow">Ask me anything</p>
              <h1 style={{ fontSize: "clamp(1.7rem, 4vw, 2.2rem)", margin: "0.3rem 0 0.4rem", letterSpacing: "-0.01em" }} data-testid="ask-title">
                Ask Joey Anything
              </h1>
              <p style={{ margin: 0, color: "var(--midnight)" }}>
                Got a question about trademarks, naming, contracts, or copycats? Ask away. Plain English answers, no legalese.
              </p>
            </div>
            <button type="button" onClick={clearChat} className="btn-outline-navy" data-testid="ask-clear-button" style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}>
              Start fresh →
            </button>
          </div>
        </div>
      </section>

      {/* Chat thread */}
      <section style={{ padding: "0 0 1rem" }}>
        <div className="container-narrow">
          <div
            data-testid="ask-thread"
            style={{
              background: "var(--paper)",
              border: "1px solid var(--hairline)",
              borderRadius: 3,
              padding: "1.5rem",
              minHeight: "55vh",
              maxHeight: "65vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            {loadingHistory && (
              <p style={{ color: "var(--cobalt)", margin: 0 }}>Loading...</p>
            )}
            {messages.map((m, i) => (
              <ChatBubble key={`${m.role}-${m.ts || i}`} message={m} idx={i} firstName={user.first_name} />
            ))}

            {/* Starter questions — only when chat is fresh */}
            {!loadingHistory && !sending && messages.length === 1 && messages[0].role === "assistant" && (
              <div data-testid="ask-starter-questions" style={{ marginLeft: 50, marginTop: -4 }}>
                <p className="eyebrow" style={{ marginBottom: 8 }}>Try one of these</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {STARTER_QUESTIONS.map((q, i) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => sendStarter(q)}
                      data-testid={`ask-starter-${i}`}
                      className="starter-pill"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {sending && (
              <div style={{ display: "flex", gap: "0.7rem" }} data-testid="ask-typing">
                <img
                  src={JOEY_THINKING}
                  alt="Reviewing your question"
                  style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--navy)", flexShrink: 0 }}
                />
                <div style={{ background: "var(--warm-white-soft)", padding: "0.6rem 0.9rem", borderRadius: 3, fontStyle: "italic", color: "var(--cobalt)" }}>
                  Reviewing your question
                  <span className="typing-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        </div>
      </section>

      {/* Input */}
      <section style={{ padding: "0.5rem 0 3rem" }}>
        <div className="container-narrow">
          <form onSubmit={send} data-testid="ask-form" style={{ display: "flex", gap: "0.6rem", alignItems: "flex-end" }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask anything about trademarks, naming, contracts, copycats..."
              className="brand-input"
              data-testid="ask-input"
              rows={2}
              style={{ resize: "vertical", fontFamily: "Arial, sans-serif", flex: 1 }}
              disabled={sending}
            />
            <button
              type="submit"
              className="btn-yellow"
              disabled={sending || !input.trim()}
              data-testid="ask-send-button"
              style={{ alignSelf: "stretch", whiteSpace: "nowrap" }}
            >
              {sending ? "..." : "Send →"}
            </button>
          </form>
          <p style={{ margin: "0.6rem 0 0", fontSize: "0.8rem", color: "var(--cobalt)" }}>
            Press Enter to send · Shift+Enter for a new line
          </p>
          <Link to="/dashboard" data-testid="ask-back-to-dashboard" style={{ marginTop: "1.2rem", display: "inline-block", fontSize: "0.9rem" }}>
            ← Back to dashboard
          </Link>
        </div>
      </section>

      <style>{`
        .typing-dots span {
          animation: typing 1.4s infinite;
          display: inline-block;
        }
        .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes typing {
          0%, 60%, 100% { opacity: 0.2; }
          30% { opacity: 1; }
        }
        .starter-pill {
          background: #ffffff;
          border: 1.5px solid var(--navy);
          color: var(--navy);
          padding: 0.5rem 0.85rem;
          border-radius: 999px;
          font-family: Arial, sans-serif;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 120ms ease, color 120ms ease, transform 100ms ease;
          line-height: 1.2;
        }
        .starter-pill:hover {
          background: var(--navy);
          color: var(--warm-white);
          transform: translateY(-1px);
        }
        .starter-pill:active { transform: translateY(0); }
      `}</style>
    </div>
  );
};

const ChatBubble = ({ message, idx, firstName }) => {
  const isJoey = message.role === "assistant";
  return (
    <div
      style={{ display: "flex", gap: "0.7rem", alignItems: "flex-start", flexDirection: isJoey ? "row" : "row-reverse" }}
      data-testid={`ask-message-${idx}`}
      data-role={message.role}
    >
      {isJoey ? (
        <img
          src={JOEY_SMILE}
          alt="Joey"
          style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--navy)", flexShrink: 0 }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "var(--navy)", color: "var(--warm-white)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: "0.9rem", flexShrink: 0,
            border: "2px solid var(--navy)",
          }}
        >
          {(firstName || "Y").charAt(0).toUpperCase()}
        </div>
      )}
      <div
        style={{
          background: isJoey ? "var(--warm-white-soft)" : "var(--navy)",
          color: isJoey ? "var(--midnight)" : "var(--warm-white)",
          padding: "0.8rem 1rem",
          borderRadius: 3,
          maxWidth: "85%",
          border: isJoey ? "1px solid var(--hairline)" : "1px solid var(--navy)",
        }}
      >
        {isJoey
          ? <div style={{ fontSize: "0.97rem" }}>{renderContent(message.content)}</div>
          : <div style={{ fontSize: "0.97rem", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{message.content}</div>
        }
      </div>
    </div>
  );
};
